import { useMemo } from 'react'
import { GridColDef } from '@mui/x-data-grid-pro'
import { categories, facilityRateCodes } from './Pricebook'
import { useGetCodeSet } from '../../APIs/Hooks/CodeSetHooks'
import {
  getCodeSetLabelFromList,
  getCodeSetLabelLookup,
} from '../../Functions/CodeSetFns'
import { useUserContext } from '../../Contexts/UserConfig'

function currencyFormatter(value?: number) {
  if (value == null) {
    return ''
  }
  return `$ ${parseFloat(value.toString()).toFixed(2)}`
}
export function usePricebookCols(category: (typeof categories)[number] | null) {
  const { loginConfig } = useUserContext()
  const holdingTypeQuery = useGetCodeSet('HoldingTypeID')
  const lineTypeQuery = useGetCodeSet('LineTypeID')
  const organTypeQuery = useGetCodeSet('OrganTypeID')
  const serviceTypeQuery = useGetCodeSet('ServiceTypeID')

  return useMemo<GridColDef[]>(() => {
    const defaultCols: GridColDef[] = [
      {
        headerName: 'AuditID',
        field: 'AuditID',
      },
      {
        headerName: 'HoldingRateID',
        field: 'HoldingRateID',
      },
      {
        headerName: 'IssueRateID',
        field: 'IssueRateID',
      },
      {
        headerName: 'ServiceRateID',
        field: 'ServiceRateID',
      },
      {
        headerName: 'ShippingRateID',
        field: 'ShippingRateID',
      },
      {
        headerName: 'Rate Code',
        field: 'RateCode',
        width: 200,
        valueFormatter: (value?: number) =>
          value ? facilityRateCodes?.[value]?.short : '',
      },
      {
        headerName: 'Current Rate',
        field: 'Rate',
        width: 100,
        valueFormatter: (value?: number) => currencyFormatter(value),
      },
      {
        headerName: 'New Rate',
        field: 'NewRate',
        width: 100,
        cellClassName: 'new-rate',
        valueFormatter: (value?: number) => currencyFormatter(value),
      },
    ]
    const fromFac: GridColDef = {
      headerName: 'From Facility',
      field: 'FromFacilityID',
      flex: 1,
      minWidth: 100,
      valueFormatter: (value: any) =>
        getCodeSetLabelLookup(loginConfig, ['KV-Facilities'], value),
    }
    const toFac: GridColDef = {
      headerName: 'To Facility',
      field: 'ToFacilityID',
      flex: 1,
      minWidth: 100,
      valueFormatter: (value: any) =>
        getCodeSetLabelLookup(loginConfig, ['KV-Facilities'], value),
    }
    const holdType: GridColDef = {
      headerName: 'Holding Type',
      field: 'HoldingType',
      flex: 1,
      minWidth: 100,
      valueFormatter: (value: any) =>
        getCodeSetLabelFromList(holdingTypeQuery.data ?? [], value),
    }
    const lineTypeID: GridColDef = {
      headerName: 'Line Type',
      field: 'LineTypeID',
      flex: 1,
      minWidth: 100,
      valueFormatter: (value: any) =>
        getCodeSetLabelFromList(lineTypeQuery.data ?? [], value),
    }
    const organTypeID: GridColDef = {
      headerName: 'Organ Type',
      field: 'OrganTypeID',
      flex: 1,
      minWidth: 100,
      valueFormatter: (value: any) =>
        getCodeSetLabelFromList(organTypeQuery.data ?? [], value),
    }
    const age: GridColDef = {
      headerName: 'Age',
      field: 'Age',
      flex: 1,
      minWidth: 100,
    }
    const lineType: GridColDef = {
      headerName: 'Line Type',
      field: 'LineType',
      flex: 1,
      minWidth: 150,
      valueFormatter: (value: any) =>
        getCodeSetLabelFromList(lineTypeQuery.data ?? [], value),
    }
    const serviceType: GridColDef = {
      headerName: 'Service Type',
      field: 'ServiceType',
      flex: 1,
      minWidth: 150,
      valueFormatter: (value: any) =>
        getCodeSetLabelFromList(serviceTypeQuery.data ?? [], value),
    }
    const issueDesc: GridColDef = {
      headerName: 'Description',
      field: 'IssueDescription',
      flex: 1,
      minWidth: 150,
    }
    const species: GridColDef = {
      headerName: 'Species',
      field: 'Species',
      flex: 1,
      minWidth: 150,
    }
    const servBillUnit: GridColDef = {
      headerName: 'Service Billing Unit',
      field: 'ServiceBillingUnit',
      flex: 1,
      minWidth: 150,
    }
    const item: GridColDef = {
      headerName: 'Item',
      field: 'Item',
      flex: 1,
    }
    switch (category) {
      case 'Holding':
        return [holdType, ...defaultCols]
      case 'Issue':
        return [age, issueDesc, lineType, species, ...defaultCols]
      case 'Service':
        return [servBillUnit, serviceType, ...defaultCols]
      case 'Shipper':
        return [fromFac, toFac, ...defaultCols]
      case 'Shipping':
        return [item, ...defaultCols]
      case 'Tissue':
        return [lineTypeID, organTypeID, ...defaultCols]
      default:
        return defaultCols
    }
  }, [
    category,
    holdingTypeQuery,
    serviceTypeQuery,
    organTypeQuery,
    lineTypeQuery,
    loginConfig,
  ])
}
